import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ISSUE_REPORT_NOTE_MAX_LENGTH,
  ISSUE_REPORT_REASONS,
} from '../../../constants/games/gameIssueReports';
import { submitGameIssueReport } from '../../../utils/gameIssueReportApi';
import { getPlaceholderImageUrl } from '../../../utils/games/unlimitedGameImages';
import { INITIAL_ZOOM as UNLIMITED_INITIAL_ZOOM } from '../../../constants/games/unlimitedGame';
import DefinitionSection from '../common/DefinitionSection';
import AnsweredGridPreview from '../shared/AnsweredGridPreview';
import ZoomedImagePreview from '../shared/ZoomedImagePreview';
import ShareZoomedImageButton from '../shared/ShareZoomedImageButton';
import RewardSummary from '../shared/RewardSummary';

const UnlimitedGameModal = ({
  modalState,
  stats,
  user,
  gameState,
  showDefinition,
  definition,
  isLoadingDefinition,
  toggleDefinition,
  resetGame,
  countdown,
  guestGameLimitReached = false,
  guestGamesPlayedToday = 0,
  guestDailyGameLimit = 3,
}) => {
  const [showReportForm, setShowReportForm] = useState(false);
  const [selectedReason, setSelectedReason] = useState(
    ISSUE_REPORT_REASONS[0]?.value || 'image_word_mismatch'
  );
  const [impactedResult, setImpactedResult] = useState(false);
  const [note, setNote] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportSuccess, setReportSuccess] = useState(false);
  const [reportError, setReportError] = useState('');

  const wins = Object.values(stats.guesses).reduce((a, b) => a + b, 0);
  const losses = stats.losses || 0;
  const totalGames = wins + losses;
  const winPercentage = totalGames > 0 ? Math.round((wins / totalGames) * 100) : 0;
  const maxGuesses = Math.max(...Object.values(stats.guesses));
  const rewardCoins =
    typeof modalState?.rewardCoins === 'number' ? modalState.rewardCoins : null;
  const pointsAwarded = modalState?.pointsAwarded ?? 0;
  const firstGameBonus = modalState?.firstGameBonus ?? false;
  const isWin = Boolean(modalState?.isWin);
  const visibleRewardCoins = isWin ? rewardCoins : null;
  const previewZoomLevel = isWin ? 100 : (modalState?.shareZoomLevel ?? 100);
  const previewZoomOrigin = isWin ? null : (modalState?.shareZoomOrigin ?? null);
  const copyZoomLevel = UNLIMITED_INITIAL_ZOOM;
  const copyZoomOrigin = modalState?.shareZoomOrigin ?? null;
  const remainingNoteChars = useMemo(
    () => ISSUE_REPORT_NOTE_MAX_LENGTH - note.length,
    [note]
  );
  const canReport = Boolean(gameState.solution);

  const resetReportState = () => {
    setSelectedReason(ISSUE_REPORT_REASONS[0]?.value || 'image_word_mismatch');
    setImpactedResult(false);
    setNote('');
    setReportError('');
  };

  const onToggleReportForm = () => {
    if (reportSubmitting) return;
    const next = !showReportForm;
    setShowReportForm(next);
    if (!next && !reportSuccess) {
      resetReportState();
    }
  };

  const onSubmitReport = async (event) => {
    event.preventDefault();
    if (reportSubmitting || reportSuccess || !canReport) return;

    setReportSubmitting(true);
    setReportError('');

    try {
      await submitGameIssueReport({
        gameType: 'unlimited',
        roundId: modalState?.reportRoundId ?? null,
        reason: selectedReason,
        impactedResult,
        note: note.trim(),
        word: gameState.solution,
        imageSource: gameState.imageSource,
        imageId: gameState.imageId,
        imageUrl: gameState.imageUrl,
      });
      setReportSuccess(true);
    } catch (error) {
      setReportError(
        error?.userMessage ||
          error?.message ||
          'Could not submit the report right now. Please try again.'
      );
    } finally {
      setReportSubmitting(false);
    }
  };

  return (
    <div className="modal-content game-result-modal game-result-modal--unlimited game-result-modal--scrollbar">
      <h2>{modalState.title}</h2>
      <RewardSummary
        rewardCoins={visibleRewardCoins}
        pointsAwarded={pointsAwarded}
        firstGameBonus={firstGameBonus}
        showSignInHint={!user && typeof visibleRewardCoins === 'number' && visibleRewardCoins > 0}
      />
      <div className="modal-image-container game-result-modal__media">
        <ZoomedImagePreview
          imageUrl={gameState.imageUrl || getPlaceholderImageUrl(gameState.solution)}
          alt={gameState.solution}
          zoomLevel={previewZoomLevel}
          zoomOrigin={previewZoomOrigin}
          size={240}
          borderRadius={10}
        />
        <p>
          Word: <strong>{gameState.solution.toUpperCase()}</strong>
        </p>
        <AnsweredGridPreview guesses={gameState.guesses} solution={gameState.solution} />
        <DefinitionSection
          word={gameState.solution}
          showDefinition={showDefinition}
          definition={definition}
          isLoadingDefinition={isLoadingDefinition}
          toggleDefinition={toggleDefinition}
        />
        <ShareZoomedImageButton
          imageUrl={gameState.imageUrl}
          solution={gameState.solution}
          zoomLevel={copyZoomLevel}
          zoomOrigin={copyZoomOrigin}
          gameName="Zoomed In Unlimited"
          guesses={gameState.guesses}
          maxGuesses={5}
          disabled={!gameState.imageUrl}
        />
        <div className="game-report">
          <button
            type="button"
            className="game-report__toggle"
            onClick={onToggleReportForm}
            disabled={!canReport || reportSubmitting}
          >
            {showReportForm ? 'Hide report form' : reportSuccess ? 'Issue reported' : 'Report issue'}
          </button>

          {showReportForm && !reportSuccess && (
            <form className="game-report__form" onSubmit={onSubmitReport}>
              <p className="game-report__hint">
                Report image and word mismatch, broken media, or other round issues.
              </p>

              <fieldset className="game-report__reasons">
                <legend>Issue type</legend>
                {ISSUE_REPORT_REASONS.map((reason) => (
                  <label key={reason.value} className="game-report__reason-option">
                    <input
                      type="radio"
                      name="game-report-reason"
                      value={reason.value}
                      checked={selectedReason === reason.value}
                      onChange={(e) => setSelectedReason(e.target.value)}
                      disabled={reportSubmitting}
                    />
                    <span>{reason.label}</span>
                  </label>
                ))}
              </fieldset>

              <label className="game-report__impact">
                <input
                  type="checkbox"
                  checked={impactedResult}
                  onChange={(e) => setImpactedResult(e.target.checked)}
                  disabled={reportSubmitting}
                />
                <span>This affected my game result</span>
              </label>

              <label className="game-report__note-label" htmlFor="game-report-note">
                Notes (optional)
              </label>
              <textarea
                id="game-report-note"
                className="game-report__note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={ISSUE_REPORT_NOTE_MAX_LENGTH}
                rows={3}
                placeholder="Add details to help diagnose the issue."
                disabled={reportSubmitting}
              />
              <div className="game-report__note-meta">
                {remainingNoteChars} characters left
              </div>

              <button
                type="submit"
                className="game-report__submit"
                disabled={reportSubmitting}
              >
                {reportSubmitting ? 'Submitting...' : 'Submit report'}
              </button>

              {reportError && <p className="game-report__error">{reportError}</p>}
            </form>
          )}

          {showReportForm && reportSuccess && (
            <p className="game-report__success">
              Thanks. Your report was submitted for review.
            </p>
          )}
        </div>
      </div>
      {user ? (
        <div className="stats-container">
          <div className="stats-summary">
            <div className="stat-box">
              <span className="stat-number">{stats.currentStreak}</span>
              <span className="stat-label">Streak</span>
            </div>
            <div className="stat-box">
              <span className="stat-number">{stats.maxStreak}</span>
              <span className="stat-label">Max Streak</span>
            </div>
            <div className="stat-box">
              <span className="stat-number">{totalGames}</span>
              <span className="stat-label">Played</span>
            </div>
            <div className="stat-box">
              <span className="stat-number">{winPercentage}</span>
              <span className="stat-label">Win %</span>
            </div>
          </div>
          <div className="guess-distribution">
            <h3>Guess Distribution</h3>
            {[1, 2, 3, 4, 5].map((guessNumber) => {
              const percentage = maxGuesses
                ? (stats.guesses[guessNumber] / maxGuesses) * 90
                : 0;
              return (
                <div key={guessNumber} className="guess-row">
                  <div className="guess-label">{guessNumber}</div>
                  <div className="guess-bar-container">
                    <div
                      className="guess-bar"
                      style={{
                        width: `${Math.min(90, percentage)}%`,
                      }}
                    >
                      {stats.guesses[guessNumber] || 0}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="stats-container">
          <div className="stats-summary">
            <div className="stat-box">
              <span className="stat-label">Sign in to track streaks and stats.</span>
            </div>
          </div>
          {guestGameLimitReached && (
            <p>
              Daily guest limit reached ({guestGamesPlayedToday}/{guestDailyGameLimit}).
              Sign in to keep playing.
            </p>
          )}
          <div className="modal-buttons">
            <Link to="/auth" className="modal-button">
              Sign in / Sign up
            </Link>
          </div>
        </div>
      )}
      <button
        className="modal-button"
        onClick={resetGame}
        disabled={countdown > 0}
        style={{
          opacity: countdown > 0 ? 0.5 : 1,
          cursor: countdown > 0 ? 'not-allowed' : 'pointer',
        }}
      >
        {countdown > 0
          ? `Next Word (${countdown})`
          : guestGameLimitReached
            ? 'Create Account To Continue'
            : 'Next Word'}
      </button>
    </div>
  );
};

export default UnlimitedGameModal;
